import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Upload, GripVertical } from "lucide-react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";

export const Route = createFileRoute("/admin/gallery")({
  component: AdminGallery,
});

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.setAttribute("crossOrigin", "anonymous");
    img.src = url;
  });
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas not supported");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.95));
  if (!blob) throw new Error("crop failed");
  return new File([blob], "cropped.jpg", { type: "image/jpeg" });
}

function AdminGallery() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["gallery"],
    queryFn: () => api.gallery.list(),
  });

  const albums: string[] = data?.albums ?? [];
  const images: any[] = data?.images ?? [];

  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [album, setAlbum] = useState("כללי");
  const [newAlbumName, setNewAlbumName] = useState("");

  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.gallery.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gallery"] }); toast.success("תמונה נמחקה"); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, caption, sort_order, album }: { id: number; caption: string; sort_order: number; album: string }) =>
      api.gallery.update(id, caption, sort_order, album),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["gallery"] }); toast.success("עודכן"); },
  });

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("בחר קובץ תמונה"); return; }
    setOriginalFile(file);
    const url = URL.createObjectURL(file);
    setCropImage(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
  }

  async function handleCropUpload() {
    if (!cropImage || !croppedArea || !originalFile) return;
    const finalAlbum = newAlbumName.trim() || album;
    setUploading(true);
    try {
      const croppedFile = await getCroppedImg(cropImage, croppedArea);
      const url = await api.upload(croppedFile);
      if (!url) throw new Error("upload failed");
      await api.gallery.add(url, caption || undefined, images.length, finalAlbum);
      qc.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("תמונה נוספה לגלריה");
      setCaption("");
      setNewAlbumName("");
      setAlbum("כללי");
      closeCropper();
    } catch (err) {
      toast.error("העלאה נכשלה");
    } finally {
      setUploading(false);
    }
  }

  function closeCropper() {
    if (cropImage) URL.revokeObjectURL(cropImage);
    setCropImage(null);
    setCroppedArea(null);
    setOriginalFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div className="max-w-4xl">
      <h1 className="font-display text-3xl font-bold text-primary mb-2">ניהול גלריה</h1>
      <p className="text-muted-foreground mb-6">העלה תמונות, הוסף כיתובים ומחק תמונות.</p>

      <div className="rounded-lg bg-card border border-border p-6 mb-8 space-y-4">
        <h2 className="font-display text-xl font-bold text-primary">הוסף תמונה</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="gallery-file">קובץ תמונה</Label>
            <Input id="gallery-file" type="file" accept="image/*" ref={fileRef} onChange={handleFileChange} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="gallery-caption">כיתוב (אופציונלי)</Label>
            <Input
              id="gallery-caption"
              placeholder="תיאור התמונה"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="gallery-album">אלבום</Label>
            <select
              id="gallery-album"
              value={newAlbumName ? "__new__" : album}
              onChange={(e) => {
                if (e.target.value === "__new__") { setNewAlbumName(" "); }
                else { setAlbum(e.target.value); setNewAlbumName(""); }
              }}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {albums.map((a) => <option key={a} value={a}>{a}</option>)}
              <option value="כללי">כללי</option>
              <option value="__new__">+ אלבום חדש</option>
            </select>
          </div>
          {(newAlbumName.trim() !== "" || newAlbumName === " ") && (
            <div>
              <Label htmlFor="gallery-new-album">שם האלבום החדש</Label>
              <Input
                id="gallery-new-album"
                placeholder="שם האלבום"
                value={newAlbumName.trim()}
                onChange={(e) => setNewAlbumName(e.target.value)}
                className="mt-1"
              />
            </div>
          )}
        </div>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
          <Upload className="h-4 w-4 ml-1" />
          בחר תמונה
        </Button>
      </div>

      {cropImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="חיתוך תמונה">
          <div className="bg-card w-full max-w-2xl rounded-xl border border-border p-4 space-y-4">
            <h3 className="font-display text-xl font-bold text-primary">חתוך את התמונה</h3>
            <div className="relative w-full h-80 rounded-lg overflow-hidden bg-black">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">זום:</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1"
                aria-label="זום תמונה"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={closeCropper} disabled={uploading}>
                ביטול
              </Button>
              <Button onClick={handleCropUpload} disabled={uploading || !croppedArea}>
                {uploading ? "מעלה..." : "חתוך והעלה"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {isLoading && <div className="text-muted-foreground">טוען...</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((img: any) => (
          <div key={img.id} className="group relative rounded-lg overflow-hidden border border-border bg-card shadow-card">
            <img
              src={img.url}
              alt={img.caption ?? "תמונה"}
              className="w-full h-40 object-cover"
              loading="lazy"
            />
            <div className="p-3 space-y-2">
              <Input
                defaultValue={img.caption ?? ""}
                placeholder="כיתוב"
                aria-label="כיתוב תמונה"
                onBlur={(e) => {
                  if (e.target.value !== (img.caption ?? "")) {
                    updateMutation.mutate({ id: img.id, caption: e.target.value, sort_order: img.sort_order ?? 0, album: img.album ?? "כללי" });
                  }
                }}
                className="text-sm h-8"
              />
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  defaultValue={img.sort_order ?? 0}
                  aria-label="סדר מיון"
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val !== img.sort_order) {
                      updateMutation.mutate({ id: img.id, caption: img.caption ?? "", sort_order: val, album: img.album ?? "כללי" });
                    }
                  }}
                  className="text-sm h-8 w-20"
                />
                <span className="text-xs text-muted-foreground flex-1">סדר</span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => { if (confirm("למחוק תמונה זו?")) deleteMutation.mutate(img.id); }}
                  aria-label="מחק תמונה"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!isLoading && images.length === 0 && (
        <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-lg">
          אין תמונות בגלריה עדיין. העלה את הראשונה!
        </div>
      )}
    </div>
  );
}
